import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ShareIntakeLinkButton } from "./ShareIntakeLinkButton";
import { RoleShell, type RoleShellNavItem, type RoleShellNavSection } from "./RoleShell";
import { recruiterApi } from "../lib/api";
import { recruiterHasExploreSeat, recruiterKycNavLabel, recruiterMarketplaceOpen, recruiterNeedsKycPack } from "../lib/kyc";
import { NAV_COPY, NAV_ICONS } from "../lib/navCopy";
import { navHasCandidatePages } from "../utils/navActive";

const OVERVIEW: RoleShellNavSection = {
  label: NAV_COPY.sections.overview,
  items: [{ to: "/", label: NAV_COPY.common.dashboard, icon: NAV_ICONS.dashboard, end: true }],
};

const CALL_QUEUE_ITEM: RoleShellNavItem = {
  to: "/explore",
  label: NAV_COPY.recruiter.callQueue,
  icon: NAV_ICONS.callQueue,
};

const SOURCING: RoleShellNavSection = {
  label: NAV_COPY.sections.sourcing,
  items: [
    CALL_QUEUE_ITEM,
    { to: "/jobs", label: NAV_COPY.common.openJobs, icon: NAV_ICONS.openJobs },
    { to: "/candidates", label: NAV_COPY.recruiter.myCandidates, icon: NAV_ICONS.candidates, exclude: "/candidates/new" },
    { to: "/candidates/new", label: NAV_COPY.recruiter.addCandidate, icon: NAV_ICONS.addCandidate, exact: true },
  ],
};

const PIPELINE: RoleShellNavSection = {
  label: NAV_COPY.sections.pipeline,
  items: [
    { to: "/submissions", label: NAV_COPY.common.submissions, icon: NAV_ICONS.submissions },
    { to: "/disputes", label: NAV_COPY.common.disputes, icon: NAV_ICONS.disputes },
  ],
};

const FINANCE: RoleShellNavSection = {
  label: NAV_COPY.sections.finance,
  items: [{ to: "/payments", label: NAV_COPY.common.payments, icon: NAV_ICONS.payments }],
};

export function AppLayout() {
  const profileQuery = useQuery({ queryKey: ["my-profile"], queryFn: recruiterApi.myProfile });
  const affiliated = Boolean(profileQuery.data?.consultancy_id);
  const consultancyName = profileQuery.data?.consultancy_name;
  const showKycNav = !recruiterMarketplaceOpen(profileQuery.data) || recruiterNeedsKycPack(profileQuery.data);
  const kycIndicator =
    profileQuery.data?.kyc_status === "submitted"
      ? { label: "In review", color: "blue" }
      : profileQuery.data?.kyc_status === "rejected"
        ? { label: "Fix required", color: "red" }
        : profileQuery.data?.kyc_status === "verified"
          ? { label: "Docs needed", color: "orange" }
          : { label: "Action needed", color: "orange" };

  const navSections = useMemo((): RoleShellNavSection[] => {
    const showExplore = !profileQuery.isFetched || recruiterHasExploreSeat(profileQuery.data);
    const sourcingItems = showExplore
      ? SOURCING.items
      : SOURCING.items.filter((item) => item.to !== CALL_QUEUE_ITEM.to);
    const overview = showKycNav
      ? {
          ...OVERVIEW,
          items: [
            ...OVERVIEW.items,
            {
              to: "/kyc",
              label: recruiterKycNavLabel(profileQuery.data),
              icon: NAV_ICONS.kyc,
              color: profileQuery.data?.kyc_status === "rejected" ? "red" : "orange",
              badge: true,
              badgeLabel: kycIndicator.label,
              badgeColor: kycIndicator.color,
              attention:
                profileQuery.data?.kyc_status !== "submitted" &&
                !(profileQuery.data?.kyc_status === "verified" && !recruiterNeedsKycPack(profileQuery.data)),
            },
          ],
        }
      : OVERVIEW;
    const finance = affiliated ? null : FINANCE;
    return [overview, { ...SOURCING, items: sourcingItems }, PIPELINE, ...(finance ? [finance] : [])];
  }, [affiliated, kycIndicator.color, kycIndicator.label, profileQuery.data, profileQuery.isFetched, showKycNav]);

  const showShareLink = navHasCandidatePages(navSections.flatMap((section) => section.items));

  return (
    <RoleShell
      portalLabel={consultancyName ? `${consultancyName} Recruiter Portal` : "Recruiter Portal"}
      logoTo="/"
      navSections={navSections}
      profileTo="/profile"
      avatarFallback="R"
      navbarExtra={
        showShareLink ? (
          <ShareIntakeLinkButton
            label="Share candidate link"
            variant="subtle"
            size="sm"
            fullWidth
          />
        ) : undefined
      }
    />
  );
}
